import axios, { AxiosRequestConfig } from 'axios'
import * as D from 'io-ts/lib/Decoder'
import { isLeft, Left } from 'fp-ts/lib/Either'
import { draw } from 'io-ts/lib/Tree'
import { ApiResult, decodeError, httpError, success } from 'data/ApiResult'

type Method = 'get' | 'post' | 'put' | 'delete' | 'patch'

type AxiosArg0<T = any> = {
    get: AxiosRequestConfig
    delete: AxiosRequestConfig
    post: T
    put: T
    patch: T
}

type AxiosArg1 = {
    get: never
    delete: never
    post: AxiosRequestConfig
    put: AxiosRequestConfig
    patch: AxiosRequestConfig
}

const defaultDecoder = {
    decode: () => D.success(undefined!),
}

const makeApi = <M extends Method, I, O>(method: M, path: string, decoder: D.Decoder<O> = defaultDecoder) => async (
    axiosArg0?: AxiosArg0<I>[M],
    axiosArg1?: AxiosArg1[M],
): Promise<ApiResult<O>> => {
    const axiosFn = axios[method] as typeof axios.post
    try {
        const response = await axiosFn<unknown>(`${path}`, axiosArg0, axiosArg1)
        const result = (decoder || defaultDecoder).decode(response.data)

        if (response.status === 204) {
            const msg = 'Failed to decode API result: cannot decode a 204 response'
            //eslint-disable-next-line
            console.warn(msg)
            return decodeError((D.failure(msg) as Left<D.DecodeError>).left)
        }

        if (isLeft(result)) {
            console.error('Failed to decode API result \n\n', draw(result.left))
            return decodeError(result.left)
        }

        return success(result.right)
    } catch (error) {
        return httpError(error)
    }
}

export default {
    get: <O>(path: string, decoder: D.Decoder<O>) => makeApi<'get', null, O>('get', path, decoder),
    delete: <O>(path: string, decoder: D.Decoder<O>) => makeApi<'delete', null, O>('delete', path, decoder),
    post: <I, O = undefined>(path: string, decoder?: D.Decoder<O>) => makeApi<'post', I, O>('post', path, decoder),
    put: <I, O = undefined>(path: string, decoder?: D.Decoder<O>) => makeApi<'put', I, O>('put', path, decoder),
    patch: <I, O = undefined>(path: string, decoder?: D.Decoder<O>) => makeApi<'patch', I, O>('patch', path, decoder),
}
